import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useSavedProducts } from '../hooks/useSavedProducts';
import { useCategories } from '../hooks/useProducts';
import { productApi } from '../api/productApi';
import AssistantWidget from './AssistantWidget';
import TopProgressBar from './TopProgressBar';

const navClass = ({ isActive }) => `nav-link${isActive ? ' active' : ''}`;

// Debounce the raw input so we don't hit the API on every keystroke.
function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function SiteLayout() {
  const navigate = useNavigate();
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const { itemCount } = useCart();
  const { savedCount } = useSavedProducts();
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // Live suggestions from the store API (searchCatalog demo data showed
  // products that don't exist in the real inventory).
  const debouncedQuery = useDebounced(query.trim());
  const { data: suggestData } = useQuery({
    queryKey: ['products', 'suggest', debouncedQuery],
    queryFn: () => productApi.getProducts({ keyword: debouncedQuery, limit: 4 }),
    enabled: searchOpen && debouncedQuery.length >= 2,
    staleTime: 60 * 1000,
    keepPreviousData: true,
  });
  const suggestions = debouncedQuery.length >= 2 ? (suggestData?.products || []) : [];

  // Nav shows only categories that actually have live products. Brand links
  // live in the catalogue filter + footer to keep the top bar uncluttered.
  const { data: navCatData } = useCategories();
  const navCategories = (navCatData?.categories || []).slice(0, 4);
  const assistantEnabled = import.meta.env.VITE_AI_ASSISTANT_ENABLED === 'true';

  // Smooth page transitions: re-key the routed view on each path change so it
  // fades in, and jump to the top so a new page never opens mid-scroll.
  const location = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0 }); }, [location.pathname]);

  const search = (event) => {
    event.preventDefault();
    navigate(`/products${query.trim() ? `?keyword=${encodeURIComponent(query.trim())}` : ''}`);
    setMenuOpen(false);
    setSearchOpen(false);
  };

  const signOut = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="site-shell">
      <TopProgressBar />
      <div className="offer-bar">
        <div className="container offer-bar-inner">
          <span>Genuine products • Clear invoices • Brand warranty</span>
          <span className="offer-hide">Fast local help from our Kolkata team</span>
          <a href="tel:+919831166129">Call +91 98311 66129</a>
        </div>
      </div>

      <header className="site-header">
        <div className="container header-main">
          <Link to="/" className="brand-link" aria-label="Kolkata Electronics home">
            <img className="brand-full" src="/brand/logo.png" alt="Kolkata Electronics" />
            <img className="brand-icon" src="/brand/icon.png" alt="" aria-hidden="true" />
          </Link>

          <form className="header-search" onSubmit={search} role="search" onFocus={() => setSearchOpen(true)} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setSearchOpen(false); }}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m21 21-4.4-4.4m2.4-5.1a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z" /></svg>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search mobiles, audio, accessories..." aria-label="Search products" />
            <button type="submit">Search</button>
            {searchOpen && query.trim() && (
              <div className="search-suggestions">
                {suggestions.length ? suggestions.map((product) => (
                  <Link key={product._id} to={`/products/${product.slug || product._id}`} onClick={() => { setQuery(''); setSearchOpen(false); }}>
                    <span>{product.images?.[0] ? <img src={product.images[0]} alt="" loading="lazy" style={{ width: 28, height: 28, objectFit: 'contain' }} /> : product.emoji}</span><span><strong>{product.name}</strong><small>{product.brand} · ₹{product.price.toLocaleString('en-IN')}</small></span><b>→</b>
                  </Link>
                )) : <p>No quick matches. Press Search to see all results.</p>}
                <button type="submit" className="search-all">See all results for “{query.trim()}”</button>
              </div>
            )}
          </form>

          <div className="header-actions">
            {isLoggedIn ? (
              <div className="account-menu">
                <button type="button" className="header-action" aria-haspopup="menu" aria-expanded={accountOpen}
                  onClick={() => setAccountOpen((v) => !v)}>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 21a8 8 0 0 0-16 0m8-9a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" /></svg>
                  <span><small>Welcome</small>{user?.name?.split(' ')[0] || 'Account'}</span>
                </button>
                {accountOpen && (
                  <>
                    <div className="menu-backdrop" onClick={() => setAccountOpen(false)} />
                    <div className="account-dropdown" role="menu">
                      <Link to="/profile" role="menuitem" onClick={() => setAccountOpen(false)}>My account</Link>
                      <Link to="/orders" role="menuitem" onClick={() => setAccountOpen(false)}>My orders</Link>
                      <Link to="/saved" role="menuitem" onClick={() => setAccountOpen(false)}>Saved products</Link>
                      {isAdmin && <Link to="/admin" role="menuitem" onClick={() => setAccountOpen(false)}>Admin dashboard</Link>}
                      <button type="button" role="menuitem" className="account-signout" onClick={() => { setAccountOpen(false); signOut(); }}>Sign out</button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link to="/login" className="header-action" aria-label="Sign in">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 21a8 8 0 0 0-16 0m8-9a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" /></svg>
                <span><small>Account</small>Sign in</span>
              </Link>
            )}
            <Link to="/saved" className="header-action saved-action" aria-label={`Saved products${savedCount ? `, ${savedCount} items` : ''}`}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" /></svg>
              <span><small>Your</small>Saved</span>
              {savedCount > 0 && <b>{savedCount}</b>}
            </Link>
            <Link to="/cart" className="header-action cart-action" aria-label={`Shopping cart${itemCount ? `, ${itemCount} items` : ''}`}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 2-1.6L20.5 8H6m4 12h.01M17 20h.01" /></svg>
              <span><small>Your</small>Cart</span>
              {itemCount > 0 && <b>{itemCount}</b>}
            </Link>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" aria-expanded={menuOpen}>
              <span /><span /><span />
            </button>
          </div>
        </div>

        <nav className={`category-nav${menuOpen ? ' open' : ''}`} aria-label="Main navigation">
          <div className="container category-nav-inner">
            <NavLink to="/products" className={navClass} onClick={() => setMenuOpen(false)}>All products</NavLink>
            {navCategories.map((cat) => (
              <NavLink key={cat._id} to={`/products?category=${encodeURIComponent(cat._id)}`} className="nav-link" onClick={() => setMenuOpen(false)}>{cat._id}</NavLink>
            ))}
            <span className="nav-divider" aria-hidden="true" />
            <NavLink to="/compare" className={navClass} onClick={() => setMenuOpen(false)}>⚖ Compare</NavLink>
            <NavLink to="/try-before-you-buy" className={navClass} onClick={() => setMenuOpen(false)} style={{ color: 'var(--orange)', fontWeight: 700 }}>🏠 Home demo</NavLink>
            <NavLink to="/exchange" className={navClass} onClick={() => setMenuOpen(false)} style={{ color: 'var(--orange)', fontWeight: 700 }}>♻️ Exchange</NavLink>
            <NavLink to="/prebook" className={navClass} onClick={() => setMenuOpen(false)} style={{ color: 'var(--orange)', fontWeight: 700 }}>⚡ Pre-book</NavLink>
            {/* Account actions (orders, admin, sign out) live in the account menu;
                brand links live in the catalogue filter + footer — keeps this bar clean. */}
            {isLoggedIn && <NavLink to="/orders" className={({ isActive }) => `nav-link nav-orders-mobile${isActive ? ' active' : ''}`} onClick={() => setMenuOpen(false)}>My orders</NavLink>}
          </div>
        </nav>
      </header>

      <main className="site-main"><div key={location.pathname} className="route-fade"><Outlet /></div></main>

      <footer className="site-footer">
        <div className="container footer-grid">
          <div className="footer-brand">
            <div className="footer-logo-panel"><img src="/brand/logo.png" alt="Kolkata Electronics" /></div>
            <p>Technology you can trust, from people who are nearby.</p>
          </div>
          <div><h3>Shop</h3><Link to="/products">All products</Link><Link to="/products?category=Mobiles">Mobiles</Link><Link to="/products?category=Tablets">Tablets</Link><Link to="/products?category=Accessories">Accessories</Link></div>
          <div><h3>Brands</h3><Link to="/brands/apple">Apple</Link><Link to="/brands/samsung">Samsung</Link><Link to="/brands/nothing">Nothing</Link><Link to="/brands/realme">realme</Link><Link to="/brands/xiaomi">Xiaomi</Link><Link to="/brands/oppo">OPPO</Link><Link to="/brands/vivo">vivo</Link><Link to="/brands/google">Google</Link></div>
          <div><h3>Help</h3><Link to="/try-before-you-buy">Free home demo</Link><Link to="/exchange">Exchange old device</Link><Link to="/orders">Track order</Link><Link to="/profile">My account</Link><Link to="/policies/returns">Returns & refunds</Link><Link to="/policies/shipping">Shipping & delivery</Link><Link to="/policies/contact">Contact & grievance</Link></div>
          <div><h3>Legal</h3><Link to="/policies/terms">Terms & conditions</Link><Link to="/policies/privacy">Privacy policy</Link></div>
          <div className="footer-contact"><h3>Visit us</h3><p>Chandni Chowk, Kolkata<br />Mon-Sat, 10am-8pm</p><a href="tel:+919831166129">+91 98311 66129</a></div>
        </div>
        <div className="container footer-bottom"><span>© 2026 Kolkata Electronics</span><span>Right Tech. Right Price. Right Here.</span></div>
      </footer>
      {assistantEnabled && <AssistantWidget />}
    </div>
  );
}
